import type {
  ReportMemberDto,
  ReportMetaDto,
} from '@octopus-do/octopus-api-client'
import { metaActions } from '@store/reducers/manager/meta/actions'
import { useEffect, useReducer, useState } from 'react'
import { useDispatch } from 'react-redux'

import { NestClient } from '@/nest'
import type { Member, Team } from '@/types'

// The report screen loads nothing the editor loads: no canvas, no sockets, no
// project meta. The share dialog it borrows from the editor reads all of its
// state from that meta — who the members are, what the link allows, whose plan
// pays for it — so the report route fetches the meta on its own and puts it in
// the same place, without touching the canvas half of the store.
//
// It asks the new API, not the old sync backend the editor uses: sync takes any
// project without a canvas through its old-project migration and dies on the
// empty state, so asking it for a report answered 500 and restarted a worker
// every time a report was opened.

// A report has no comments and no roles: everyone on the list is there to look
// at it, which is what the dialog shows.
const toMember = (member: ReportMemberDto): Member =>
  ({
    avatar: member.avatar,
    color: member.color ?? 'collab-10',
    emailAddress: member.email,
    fullName: member.name || member.email,
    id: member.id,
    isActive: !member.pending,
    name: member.name || member.email,
    pending: member.pending,
    permission: 0,
    portable: false,
    role: '',
  }) as Member

// Only the name and the avatar of the workspace are shown, and only when the
// report lives in one.
const toTeam = (meta: ReportMetaDto): Team | null =>
  meta.workspace
    ? ({
        avatar: meta.workspace.avatar,
        id: meta.workspace.id,
        name: meta.workspace.name,
      } as Team)
    : null

// A report with no owner is a guest's, made from the generator and not saved
// yet. That is only known once the meta is in — until then it is not assumed.
export const useReportProjectMeta = (uuid?: string) => {
  const dispatch = useDispatch()
  const [ownerless, setOwnerless] = useState(false)
  const [version, reload] = useReducer((count: number) => count + 1, 0)

  useEffect(() => {
    if (!uuid) {
      return
    }

    // Being on this route is what makes it a report — the dialog must not wait
    // on a call, or depend on one that can fail, to know which project it is
    // looking at.
    dispatch(
      metaActions.setSpace({
        id: '',
        features: [],
        type: 'report',
        workspaces: [],
      })
    )

    // The id comes from the route, not from the call: the dialog builds the
    // report's own link out of it, and a failed meta fetch must not leave it
    // building that link out of nothing.
    dispatch(metaActions.updateMeta({ id: uuid }))

    let cancelled = false

    NestClient.sitemapReport
      .meta(uuid)
      .then((data) => {
        if (!cancelled) {
          setOwnerless(data.ownerId === null)
          dispatch(
            metaActions.updateMeta({
              id: uuid,
              members: data.members.map(toMember),
              ownerId: data.ownerId,
              ownerPlan: data.ownerPlan,
              team: toTeam(data),
            })
          )
        }
      })
      .catch((error) => {
        // Only the share dialog reads this, so a failure leaves the report
        // itself alone.
        console.error('Error fetching report project meta:', error)
      })

    return () => {
      cancelled = true

      // The editor assumes a canvas until its own space call says otherwise;
      // leaving a report behind in the store would greet it with an empty one.
      dispatch(
        metaActions.setSpace({
          id: '',
          features: [],
          type: 'sitemap',
          workspaces: [],
        })
      )
    }
  }, [dispatch, uuid, version])

  return { ownerless, reload }
}
